import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../store/hooks"
import { LlmConvoComponent } from "./llm-convo-component"
import { TranscriptComponent } from "./transcript-component"
import { useNavigate } from "react-router-dom"
import { createMessage } from "../api/thunks/conversations"
import { Conversation } from "../api/models/Conversation"
import { Message } from "../api/models/Message"
import { setNotification } from "../store/notifications-reducer"
import { useConversationStore } from "../store/conversation-store"
import { ImgModalState } from "./image-modal"
import { ToolbarComponent } from "./toolbar-component"
import { getQuestionSuggestions } from "../api/thunks/suggestions"
import { useEarningsReport } from "../hooks/useEarningsReport"
import { useTranscript } from "../hooks/useTranscript"
import { Effort, Model } from "../api/models/CreateMessageInput"
import { SignUpPopup } from "./signup-popup"
import { setEnlargedReport, setColumnOrder } from "../store/layout-reducer"
import { EarningsReportComponentV2 } from "./earnings-report-component-v2"
import { FloatingTabs } from "./floating-tabs"
import { FullScreenChatView } from "./full-screen-chat-view"
import { DataView } from "./data-view"

export const DashboardComponent = ({
    handleUpdateConversation,
    convos,
    setAllConvos,
    setImgModalState,
    openCodeModal,
    supportedTickers,
    handleCreateConversation,
    handleDeleteConversation,
    handleCreateArtifact,
    isShareMode,
    handleDeleteShareLink,
    handleCreateShareLink,
    onWorkflowCreated,
    expectResponse
}: {
    // Props remain the same
    handleUpdateConversation: (id: string, name: string) => void;
    isShareMode: boolean;
    supportedTickers: string[];
    convos: Conversation[],
    setAllConvos: (convos: Conversation[]) => void;
    setImgModalState: (state: ImgModalState) => void;
    openCodeModal: (code: string) => void;
    handleCreateConversation: (ticker: string) => Promise<void>;
    handleDeleteConversation: (id: string) => Promise<void>;
    handleCreateArtifact: (type: 'code' | 'image', data: string) => Promise<void>;
    handleDeleteShareLink: (conversationId: string) => void;
    handleCreateShareLink: (conversationId: string) => void;
    onWorkflowCreated?: () => void;
    expectResponse: (conversationId: string) => void;
}) => {
    document.title = "Dashboard | PocketQuant";
    const dispatch = useAppDispatch();
    const credentials = useAppSelector((state) => state.user.credentials);
    const user = useAppSelector((state) => state.user.user);
    const layout = useAppSelector((state) => state.layout);
    // The router holds route elements between renders. Subscribe here rather
    // than relying on the route's initial props so optimistic and streamed
    // messages always repaint immediately.
    const convo = useConversationStore((state) => state.currentConversation);
    const messageInProgress = useConversationStore((state) => state.messageInProgress);
    const navigate = useNavigate();

    const conversationId = convo?.id;
    const conversationLayout = conversationId ? layout[conversationId] : undefined; const [showSignUpPopup, setShowSignUpPopup] = useState(false);
    const [message, setMessage] = useState('');
    const [questionSuggestions, setQuestionSuggestions] = useState<string[]>([]);
    const [isEarningsTableView, setIsEarningsTableView] = useState(conversationLayout?.enlargedReport ?? true);
    const [activeTab, setActiveTab] = useState<'chat' | 'data'>('chat');

    // Column ordering from Redux store with fallback
    const columnOrder = conversationLayout?.columnOrder ?? ['earnings', 'chat', 'transcript'];
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

    useEffect(() => {
        if (conversationLayout) {
            setIsEarningsTableView(conversationLayout.enlargedReport);
        }
    }, [conversationLayout]);

    const handleSetIsEarningsTableView = (value: boolean) => {
        setIsEarningsTableView(value);
        if (conversationId) {
            dispatch(setEnlargedReport({ conversationId, enlarged: value }));
        }
    }

    // Use custom hooks with supportedTickers instead of convo.ticker
    const earningsReport = useEarningsReport(
        supportedTickers,
        credentials?.accessToken,
        isShareMode,
        convo?.id,
        isEarningsTableView
    );

    const transcript = useTranscript(
        supportedTickers,
        credentials?.accessToken,
        isShareMode,
        convo?.id
    );

    const handleConversationClick = (id: string) => {
        const queryParams = new URLSearchParams(window.location.search);
        queryParams.set('conversationId', id);
        navigate('/dashboard' + '?' + queryParams.toString());
        navigate(0);
    };

    const sendMessage = async (model: Model, effort?: Effort, messageOverride?: string): Promise<boolean> => {
        const messageToSend = messageOverride ?? message;
        if (!credentials) {
            setShowSignUpPopup(true);
            return false;
        }
        if (convo === undefined) {
            dispatch(setNotification({
                message: 'Please select or create a conversation',
                type: 'error'
            }));
            return false;
        }
        if (!convo || !(messageToSend.length > 0)) {
            console.log('not sending message because !convo or !messageToSend.length', !convo, !(messageToSend.length > 0))
            return false
        }
        if (useConversationStore.getState().messageInProgress) {
            dispatch(setNotification({ message: 'Please wait for the current response to finish.', type: 'info' }));
            return false;
        }

        // Generate temporary ID for immediate UI update
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // Optimistic state is owned by the conversation store so websocket
        // events and the POST response cannot overwrite one another.
        useConversationStore.getState().addOptimisticMessage(convo.id, {
            id: tempId,
            role: Message.RoleEnum.User,
            content: [{ text: messageToSend }]
        });

        // Now make the API call
        const msgId = await createMessage(convo.id, convo.version, {
            message: messageToSend,
            model: model,
            effort: effort,
            frontendContext: {
                earningsViewSettings: {
                    ticker: earningsReport.currentTicker,
                    fiscalPeriod: earningsReport.fiscalPeriod || '',
                    fiscalYear: earningsReport.fiscalYear || ''
                },
                transcriptViewSettings: {
                    ticker: transcript.currentTicker,
                    fiscalPeriod: transcript.dates.find(d => d.date === transcript.fiscalDateEnding)?.fiscalPeriod || '',
                    fiscalYear: transcript.dates.find(d => d.date === transcript.fiscalDateEnding)?.fiscalYear || ''
                }
            },
        }, credentials.accessToken, dispatch);

        if (!msgId) {
            useConversationStore.getState().removeOptimisticMessage(convo.id, tempId);
            return false;
        }

        useConversationStore.getState().confirmOptimisticMessage(convo.id, tempId, msgId);
        expectResponse(convo.id);

        return true;
    };

    async function getCitationTextDetails(conversationId: string, messageId: string, fileId: string) {
        const msg = convo?.messages?.find(x => x.id === messageId);
        if (!msg) {
            return 'error getting citation text';
        }

        const allFileSearches = msg.runDetails.flatMap((detail: any) =>
            detail.type === "tool_calls"
                ? detail.tool_calls.filter((call: any) => call.type === "file_search")
                : []
        );

        let searchesForThisFile = [];

        for (let i = 0; i < allFileSearches.length; i++) {
            for (let j = 0; j < allFileSearches[i].file_search.results.length; j++) {
                if (allFileSearches[i].file_search.results[j].file_id === fileId) {
                    searchesForThisFile.push(allFileSearches[i].file_search.results[j]);
                }
            }
        }

        let concatenatedText = "";
        for (let i = 0; i < searchesForThisFile.length; i++) {
            if (searchesForThisFile[i].content && searchesForThisFile[i].content.length > 0) {
                concatenatedText += searchesForThisFile[i].content[0].text;
            }
        }

        return concatenatedText;
    }

    // Drag and drop handlers
    const handleDragStart = (e: React.DragEvent | DragEvent, columnId: string) => {
        setDraggedColumn(columnId);
        e.dataTransfer?.setData('text/plain', columnId);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
        }
    };

    const handleDragOver = (e: React.DragEvent, columnId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumn(columnId);
    };

    const handleDragLeave = () => {
        setDragOverColumn(null);
    };

    const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
        e.preventDefault();
        const draggedColumnId = e.dataTransfer.getData('text/plain');

        if (draggedColumnId !== targetColumnId && conversationId) {
            const newOrder = [...columnOrder];
            const draggedIndex = newOrder.indexOf(draggedColumnId);
            const targetIndex = newOrder.indexOf(targetColumnId);

            // Remove dragged item and insert at target position
            newOrder.splice(draggedIndex, 1);
            newOrder.splice(targetIndex, 0, draggedColumnId);

            dispatch(setColumnOrder({ conversationId, columnOrder: newOrder }));
        }
        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    const handleDragEnd = () => {
        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    // Function to render individual columns
    const renderColumn = (columnId: string) => {
        const isDragging = draggedColumn === columnId;
        const isDragOver = dragOverColumn === columnId && draggedColumn !== columnId;
        const baseClasses = `bg-white/[0.2] rounded-lg shadow overflow-hidden transition-all duration-200 ${isDragging ? 'opacity-50 scale-105' : 'opacity-100'
            } ${isDragOver ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}`;

        // Create drag handler props for the column
        const dragProps = {
            onDragOver: (e: React.DragEvent) => handleDragOver(e, columnId),
            onDragLeave: handleDragLeave,
            onDrop: (e: React.DragEvent) => handleDrop(e, columnId),
        };

        switch (columnId) {
            case 'earnings':
                return (
                    <div
                        key="earnings"
                        className={`${baseClasses} hidden md:block draggable-column pl-4`}
                        data-column-id="earnings"
                        {...dragProps}
                    >
                        <div className="h-full overflow-y-auto">
                            <EarningsReportComponentV2
                                supportedTickers={supportedTickers}
                                setCurrentTicker={earningsReport.setCurrentTicker}
                                currentTicker={earningsReport.currentTicker}
                                earningsReports={earningsReport.reports}
                                fiscalPeriod={earningsReport.fiscalPeriod}
                                fiscalYear={earningsReport.fiscalYear}
                                setFiscalYear={earningsReport.setFiscalYear}
                                setFiscalPeriod={earningsReport.setFiscalPeriod}
                                earningsDates={earningsReport.dates}
                                onViewChange={handleSetIsEarningsTableView}
                                setTableView={(v: boolean) => {
                                    dispatch(setEnlargedReport({
                                        conversationId: convo!.id,
                                        enlarged: v
                                    }))
                                }}
                                tableView={isEarningsTableView}
                                loadNextReport={earningsReport.loadNextReport}
                            />
                        </div>
                    </div>
                );
            case 'chat':
                return (
                    <div
                        key="chat"
                        className={`${baseClasses} draggable-column`}
                        data-column-id="chat"
                        {...dragProps}
                    >
                        <div className="h-full overflow-y-auto">
                            <LlmConvoComponent
                                createShareLink={handleCreateShareLink}
                                questionSuggestions={questionSuggestions}
                                createArtifact={handleCreateArtifact}
                                openCodeModal={openCodeModal}
                                setImgModalState={setImgModalState}
                                getCitationTextDetails={getCitationTextDetails}
                                convo={convo}
                                message={message}
                                sendMessage={sendMessage}
                                setMessage={setMessage}
                                messageInProgress={messageInProgress}
                                onWorkflowCreated={onWorkflowCreated}
                            />
                        </div>
                    </div>
                );
            case 'transcript':
                return (
                    <div
                        key="transcript"
                        className={`${baseClasses} ${isEarningsTableView ? 'hidden' : 'hidden md:block'} draggable-column`}
                        data-column-id="transcript"
                        {...dragProps}
                    >
                        <div className="h-full overflow-y-auto">
                            <TranscriptComponent
                                setCurrentTicker={transcript.setCurrentTicker}
                                supportedTickers={supportedTickers}
                                ticker={transcript.currentTicker}
                                transcript={transcript.transcript}
                                currentFiscalDateEnding={transcript.fiscalDateEnding || ''}
                                setCurrentFiscalDateEnding={transcript.setCurrentFiscalDateEnding}
                                earningsDates={transcript.dates}
                            />
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        if (!convo || !credentials || questionSuggestions.length > 0) return;
        getQuestionSuggestions(convo.ticker, credentials.accessToken).then((data) => {
            setQuestionSuggestions(data);
        }).catch((e) => {
            console.error("Failed to get question suggestions", e);
        });
    }, [convo])

    // Add drag functionality to existing component headers
    useEffect(() => {
        const addDragToHeaders = () => {
            // Make earnings header draggable
            const earningsHeaders = document.querySelectorAll('.draggable-column[data-column-id="earnings"] h2, .draggable-column[data-column-id="earnings"] h1');
            earningsHeaders.forEach(header => {
                const headerElement = header as HTMLElement;
                headerElement.draggable = true;
                headerElement.style.cursor = 'move';
                headerElement.style.userSelect = 'none';

                // Add drag handle indicator
                if (!headerElement.querySelector('.drag-handle')) {
                    const dragHandle = document.createElement('span');
                    dragHandle.className = 'drag-handle text-gray-400 text-xs ml-2';
                    // dragHandle.textContent = '⋮⋮';
                    dragHandle.style.pointerEvents = 'none';
                    headerElement.appendChild(dragHandle);
                }

                headerElement.ondragstart = (e) => handleDragStart(e, 'earnings');
                headerElement.ondragend = handleDragEnd;
            });

            // Make chat header draggable
            const chatHeaders = document.querySelectorAll('.draggable-column[data-column-id="chat"] h1, .draggable-column[data-column-id="chat"] h2');
            chatHeaders.forEach(header => {
                const headerElement = header as HTMLElement;
                headerElement.draggable = true;
                headerElement.style.cursor = 'move';
                headerElement.style.userSelect = 'none';

                // Add drag handle indicator
                if (!headerElement.querySelector('.drag-handle')) {
                    const dragHandle = document.createElement('span');
                    dragHandle.className = 'drag-handle text-gray-400 text-xs ml-2';
                    // dragHandle.textContent = '⋮⋮';
                    dragHandle.style.pointerEvents = 'none';
                    headerElement.appendChild(dragHandle);
                }

                headerElement.ondragstart = (e) => handleDragStart(e, 'chat');
                headerElement.ondragend = handleDragEnd;
            });

            // Make transcript header draggable
            const transcriptHeaders = document.querySelectorAll('.draggable-column[data-column-id="transcript"] h1, .draggable-column[data-column-id="transcript"] h2');
            transcriptHeaders.forEach(header => {
                const headerElement = header as HTMLElement;
                headerElement.draggable = true;
                headerElement.style.cursor = 'move';
                headerElement.style.userSelect = 'none';

                // Add drag handle indicator
                if (!headerElement.querySelector('.drag-handle')) {
                    const dragHandle = document.createElement('span');
                    dragHandle.className = 'drag-handle text-gray-400 text-xs ml-2';
                    // dragHandle.textContent = '⋮⋮';
                    dragHandle.style.pointerEvents = 'none';
                    headerElement.appendChild(dragHandle);
                }

                headerElement.ondragstart = (e) => handleDragStart(e, 'transcript');
                headerElement.ondragend = handleDragEnd;
            });
        };

        // Add drag functionality after components mount
        const timer = setTimeout(addDragToHeaders, 100);

        return () => clearTimeout(timer);
    }, [convo, columnOrder]); // Re-run when column order changes

    return (
        <>
            <SignUpPopup closeModal={() => setShowSignUpPopup(false)} isOpen={showSignUpPopup} />
            <div className={`grid ${user?.isDemo ? 'grid-rows-[auto_1fr_auto]' : 'grid-rows-[1fr_auto]'} h-screen overflow-hidden`}>
                {/* Demo Account Notification Banner */}
                {user && user.isDemo && (
                    <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-2 mx-4 mt-1 rounded">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm">
                                        You're using a <strong>demo account</strong>. Your data and conversations are temporary.
                                        <button
                                            onClick={() => navigate('/signup')}
                                            className="ml-2 font-medium underline hover:text-blue-600"
                                        >
                                            Sign up now
                                        </button> to save your work permanently!
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Floating Tabs */}
                <FloatingTabs activeTab={activeTab} onTabChange={setActiveTab} />
                
                {/* Main Content Area */}
                <div className="p-6 pt-8 overflow-hidden h-full">
                    {activeTab === 'chat' ? (
                        <FullScreenChatView
                            convo={convo}
                            message={message}
                            setMessage={setMessage}
                            sendMessage={sendMessage}
                            questionSuggestions={questionSuggestions}
                            setImgModalState={setImgModalState}
                            openCodeModal={openCodeModal}
                            getCitationTextDetails={getCitationTextDetails}
                            handleCreateArtifact={handleCreateArtifact}
                            handleCreateShareLink={handleCreateShareLink}
                            messageInProgress={messageInProgress}
                            onWorkflowCreated={onWorkflowCreated}
                        />
                    ) : (
                        <DataView
                            supportedTickers={supportedTickers}
                            earningsReport={{
                                currentTicker: earningsReport.currentTicker,
                                setCurrentTicker: earningsReport.setCurrentTicker,
                                reports: earningsReport.reports,
                                fiscalPeriod: earningsReport.fiscalPeriod,
                                fiscalYear: earningsReport.fiscalYear,
                                setFiscalYear: earningsReport.setFiscalYear,
                                setFiscalPeriod: earningsReport.setFiscalPeriod,
                                dates: earningsReport.dates,
                                loadNextReport: earningsReport.loadNextReport,
                            }}
                            transcript={{
                                currentTicker: transcript.currentTicker,
                                setCurrentTicker: transcript.setCurrentTicker,
                                transcript: transcript.transcript,
                                fiscalDateEnding: transcript.fiscalDateEnding,
                                setCurrentFiscalDateEnding: transcript.setCurrentFiscalDateEnding,
                                dates: transcript.dates,
                            }}
                            conversationId={conversationId}
                            isEarningsTableView={isEarningsTableView}
                            handleSetIsEarningsTableView={handleSetIsEarningsTableView}
                        />
                    )}
                </div>

                {/* Bottom Row (Toolbar) */}
                <ToolbarComponent
                    handleUpdateConversation={handleUpdateConversation}
                    isShareMode={isShareMode}
                    convo={convo}
                    convos={convos}
                    handleConversationClick={handleConversationClick}
                    handleCreateConversation={handleCreateConversation}
                    supportedTickers={supportedTickers}
                    handleDeleteConversation={handleDeleteConversation}
                    handleDeleteShareLink={handleDeleteShareLink}
                    handleCreateShareLink={handleCreateShareLink}
                />
            </div>
        </>
    )
}
